import type { AuthAccountSwitchAnswerMessage, AuthAccountSwitchPendingMessage } from '~/constants/auth';
import type { OperationGate } from '~/utils/operationGate';

import {
  AUTH_ACCOUNT_SWITCH_HOLDING_MESSAGE,
  AUTH_ACCOUNT_SWITCH_PENDING_MESSAGE,
  AUTH_ACCOUNT_SWITCH_REFUSED_MESSAGE,
  AUTH_ACCOUNT_SWITCH_SETTLED_MESSAGE,
  AUTH_BROADCAST_CHANNEL,
} from '~/constants/auth';
import { DEFAULT_MAX_WAIT_MS } from '~/utils/operationGate';
import { ulid } from '~/utils/ulid';

/**
 * Every open tab shares one session cookie, so a response another tab had out on the old session
 * would write it back over the new one. Before a switch the switching tab asks the others to hold
 * their requests: each answers that it is holding, then that its requests out have answered. The
 * switch waits for those answers, but only so long: a tab that has not answered within
 * `ANNOUNCE_WINDOW_MS` is not waited for, and a tab that is holding is waited for no longer than its
 * own gate waits on its requests.
 */
export const ANNOUNCE_WINDOW_MS = 150;
export const SETTLE_BOUND_MS = DEFAULT_MAX_WAIT_MS;
// past this a tab holding for a switch lets its requests go, as the switching tab may have closed
const HELD_FOR_AT_MOST_MS = 30_000;

const THIS_TAB = ulid();

const isMessage = <T extends { type: string; switchId: string; tabId: string }>(data: unknown, types: string[]): data is T =>
  typeof data === 'object' && data !== null && types.includes((data as Partial<T>).type ?? '')
  && typeof (data as Partial<T>).switchId === 'string' && typeof (data as Partial<T>).tabId === 'string';

const post = (message: AuthAccountSwitchPendingMessage) => {
  const channel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
  channel.postMessage(message);
  channel.close();
};

export const holdOtherTabsForAccountSwitch = () => {
  if (typeof BroadcastChannel === 'undefined') {
    return { settled: Promise.resolve(), release: () => {} };
  }

  const switchId = ulid();
  const channel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
  const holding = new Set<string>();
  const settledTabs = new Set<string>();

  const settled = new Promise<void>((resolve) => {
    let windowClosed = false;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const finish = () => {
      timers.forEach(clearTimeout);
      channel.close();
      resolve();
    };
    const finishIfAllSettled = () => {
      if (windowClosed && [...holding].every(tab => settledTabs.has(tab))) {
        finish();
      }
    };

    channel.onmessage = ({ data }) => {
      if (!isMessage<AuthAccountSwitchAnswerMessage>(data, [AUTH_ACCOUNT_SWITCH_HOLDING_MESSAGE, AUTH_ACCOUNT_SWITCH_SETTLED_MESSAGE]) || data.switchId !== switchId) {
        return;
      }

      holding.add(data.tabId);
      if (data.type === AUTH_ACCOUNT_SWITCH_SETTLED_MESSAGE) {
        settledTabs.add(data.tabId);
      }
      finishIfAllSettled();
    };

    timers.push(setTimeout(() => {
      windowClosed = true;
      finishIfAllSettled();
    }, ANNOUNCE_WINDOW_MS));
    timers.push(setTimeout(finish, SETTLE_BOUND_MS));
  });

  channel.postMessage({ type: AUTH_ACCOUNT_SWITCH_PENDING_MESSAGE, switchId, tabId: THIS_TAB } satisfies AuthAccountSwitchPendingMessage);

  return {
    settled,
    release: () => post({ type: AUTH_ACCOUNT_SWITCH_REFUSED_MESSAGE, switchId, tabId: THIS_TAB }),
  };
};

/** The side of a tab that is not switching: holds its gate for another tab's switch, and lets it go again. */
export const createAccountSwitchFollower = ({ gate, answer }: {
  gate: OperationGate;
  answer: (message: AuthAccountSwitchAnswerMessage) => void;
}) => {
  let heldFor: string | null = null;
  let safetyTimer: ReturnType<typeof setTimeout> | undefined;

  const release = () => {
    if (heldFor === null) {
      return;
    }
    clearTimeout(safetyTimer);
    heldFor = null;
    gate.release();
  };

  /** Returns whether the message was one of a pending switch's. */
  const handle = (data: unknown) => {
    if (!isMessage<AuthAccountSwitchPendingMessage>(data, [AUTH_ACCOUNT_SWITCH_PENDING_MESSAGE, AUTH_ACCOUNT_SWITCH_REFUSED_MESSAGE])) {
      return false;
    }

    if (data.tabId === THIS_TAB) {
      return true;
    }

    if (data.type === AUTH_ACCOUNT_SWITCH_REFUSED_MESSAGE) {
      if (data.switchId === heldFor) {
        release();
      }
      return true;
    }

    const { switchId } = data;
    heldFor = switchId;
    clearTimeout(safetyTimer);
    safetyTimer = setTimeout(release, HELD_FOR_AT_MOST_MS);
    const settling = gate.hold();
    answer({ type: AUTH_ACCOUNT_SWITCH_HOLDING_MESSAGE, switchId, tabId: THIS_TAB });
    void settling.then(() => answer({ type: AUTH_ACCOUNT_SWITCH_SETTLED_MESSAGE, switchId, tabId: THIS_TAB }));
    return true;
  };

  return { handle, release };
};
